import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import './App.css';
import axios from 'axios';

// API base URL
const API_URL = 'https://api.davidtn.org/api';

// Configure axios to include JWT token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Login Component
function Login({ onLoginSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('Attempting login with password:', password);
      console.log('API URL:', `${API_URL}/auth/login`);
      
      const response = await axios.post(`${API_URL}/auth/login`, { password });
      console.log('Login response:', response.data);
      
      const { token } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('authToken', token);
      
      // Call success callback
      onLoginSuccess();
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>🔐 Email Platform</h1>
        <p className="login-subtitle">Please enter password to continue</p>
        
        <form onSubmit={handleSubmit}>
          <div className="login-input-group">
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </div>
          
          {error && <div className="login-error">{error}</div>}
          
          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Email Template Editor Component
function EmailTemplateEditor({ API_URL }) {
  const queryClient = useQueryClient();
  const [templateContent, setTemplateContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // Fetch template content
  const { data: templateData, isLoading, refetch } = useQuery({
    queryKey: ['emailTemplate'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/templates/mail-list/content`);
      return response.data;
    },
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (templateData?.content) {
      setTemplateContent(templateData.content);
    }
  }, [templateData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (content) => {
      const response = await axios.put(`${API_URL}/templates/mail-list/content`, { content });
      return response.data;
    },
    onSuccess: () => {
      setSaveSuccess('Email template saved successfully!');
      setSaveError('');
      setIsEditing(false);
      refetch();
      queryClient.invalidateQueries(['overview']);
      setTimeout(() => setSaveSuccess(''), 3000);
    },
    onError: (error) => {
      setSaveError(error.response?.data?.error || 'Save failed');
      setSaveSuccess('');
    }
  });

  const handleSave = () => {
    saveMutation.mutate(templateContent);
  };

  const handleCancel = () => {
    setTemplateContent(templateData?.content || '');
    setIsEditing(false);
    setSaveError('');
    setSaveSuccess('');
  };

  return (
    <div className="file-stats-wrapper">
      <div className="file-manager-header">
        <h3>Email Template (mail_list.txt)</h3>
        <div className="template-actions">
          {!isEditing ? (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>
              ✏️ Edit Template
            </button>
          ) : (
            <>
              <button 
                className="save-btn" 
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : '💾 Save'}
              </button>
              <button 
                className="cancel-btn" 
                onClick={handleCancel}
                disabled={saveMutation.isPending}
              >
                ❌ Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && <div className="upload-error">{saveError}</div>}
      {saveSuccess && <div className="upload-success">{saveSuccess}</div>}

      {isLoading ? (
        <p className="muted">Loading template...</p>
      ) : (
        <div className="template-editor">
          <textarea
            className="template-textarea"
            value={templateContent}
            onChange={(e) => setTemplateContent(e.target.value)}
            disabled={!isEditing}
            rows={15}
            placeholder="Enter your email template here..."
          />
          <div className="template-info">
            <small>
              ℹ️ Use <code>[Client Name]</code> as a placeholder for the recipient's name. 
              Line breaks and spacing will be preserved in the email.
            </small>
          </div>
        </div>
      )}
    </div>
  );
}

// SendGrid & From Email settings (editable in dashboard; overrides config.env) – edit on button click only
function SendGridAndFromEmail({ API_URL }) {
  const queryClient = useQueryClient();
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/settings`);
      return res.data;
    },
  });
  const [isEditing, setIsEditing] = useState(false);
  const [fromEmail, setFromEmail] = useState('');
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (settingsData) {
      setFromEmail(settingsData.fromEmail || '');
    }
  }, [settingsData]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.put(`${API_URL}/settings`, payload);
      return res.data;
    },
    onSuccess: () => {
      setSaveSuccess('SendGrid & From Email saved. Applied immediately.');
      setSaveError('');
      setSendgridApiKey('');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      setTimeout(() => setSaveSuccess(''), 4000);
    },
    onError: (err) => {
      setSaveError(err.response?.data?.error || err.message || 'Save failed');
      setSaveSuccess('');
    },
  });

  const handleSave = () => {
    const payload = { fromEmail: fromEmail.trim() };
    if (sendgridApiKey.trim()) payload.sendgridApiKey = sendgridApiKey.trim();
    saveMutation.mutate(payload);
  };

  const handleCancel = () => {
    setFromEmail(settingsData?.fromEmail || '');
    setSendgridApiKey('');
    setSaveError('');
    setSaveSuccess('');
    setIsEditing(false);
  };

  return (
    <div className="file-stats-wrapper">
      <div className="file-manager-header">
        <h3>SendGrid & From Email</h3>
        <div className="template-actions">
          {!isEditing ? (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>
              ✏️ Edit
            </button>
          ) : (
            <>
              <button
                className="save-btn"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : '💾 Save'}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancel}
                disabled={saveMutation.isPending}
              >
                ❌ Cancel
              </button>
            </>
          )}
        </div>
      </div>
      <p className="muted" style={{ marginBottom: '10px' }}>
        Overrides config.env. {isEditing && 'Leave API key blank to keep current value.'}
      </p>
      {saveError && <div className="upload-error">{saveError}</div>}
      {saveSuccess && <div className="upload-success">{saveSuccess}</div>}
      <div className="settings-grid" style={{ maxWidth: '600px' }}>
        <div className="setting-item">
          <label>FROM_EMAIL</label>
          {isEditing ? (
            <input
              type="text"
              placeholder="e.g. noreply@yourdomain.com"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
            />
          ) : (
            <div className="setting-readonly">{settingsData?.fromEmail || '—'}</div>
          )}
        </div>
        <div className="setting-item">
          <label>SENDGRID_API_KEY</label>
          {isEditing ? (
            <>
              <input
                type="password"
                placeholder={settingsData?.sendgridApiKeySet ? '•••••••• (leave blank to keep)' : 'Enter API key'}
                value={sendgridApiKey}
                onChange={(e) => setSendgridApiKey(e.target.value)}
              />
              {settingsData?.sendgridApiKeyMasked && (
                <small className="muted">Current: {settingsData.sendgridApiKeyMasked}</small>
              )}
            </>
          ) : (
            <div className="setting-readonly">
              {settingsData?.sendgridApiKeySet ? settingsData.sendgridApiKeyMasked : '—'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Client Files Manager Component
function ClientFilesManager({ API_URL }) {
  const queryClient = useQueryClient();
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Fetch client files
  const { data: clientFiles = [], isLoading, refetch } = useQuery({
    queryKey: ['clientFiles'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/clients/files`);
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Upload mutation - now handles multiple files
  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      const formData = new FormData();
      // Append all files with the same field name
      files.forEach((file) => {
        formData.append('clientFiles', file);
      });
      const response = await axios.post(`${API_URL}/clients/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: (data) => {
      const successMsg = data.message || `${data.results?.length || 0} file(s) uploaded successfully!`;
      setUploadSuccess(successMsg);
      setUploadError('');
      if (data.errors && data.errors.length > 0) {
        const errorDetails = data.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
        setUploadError(`Some files failed:\n${errorDetails}`);
      }
      refetch();
      queryClient.invalidateQueries(['overview']);
      setTimeout(() => {
        setUploadSuccess('');
        setUploadError('');
      }, 5000);
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Upload failed';
      setUploadError(errorMsg);
      setUploadSuccess('');
    }
  });

  // Delete mutation (single file)
  const deleteMutation = useMutation({
    mutationFn: async (filename) => {
      const response = await axios.delete(`${API_URL}/clients/files/${filename}`);
      return response.data;
    },
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries(['overview']);
      setSelectedFiles([]);
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Delete failed');
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (filenames) => {
      const response = await axios.delete(`${API_URL}/clients/files`, {
        data: { filenames }
      });
      return response.data;
    },
    onSuccess: (data) => {
      const successMsg = data.message || `${data.deleted?.length || 0} file(s) deleted successfully!`;
      alert(successMsg);
      if (data.errors && data.errors.length > 0) {
        const errorDetails = data.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
        alert(`Some files failed to delete:\n${errorDetails}`);
      }
      refetch();
      queryClient.invalidateQueries(['overview']);
      setSelectedFiles([]);
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Bulk delete failed');
    }
  });

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      return;
    }

    // Validate all files are .txt
    const invalidFiles = files.filter(file => !file.name.endsWith('.txt'));
    if (invalidFiles.length > 0) {
      setUploadError(`Only .txt files are allowed. Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
      e.target.value = ''; // Reset input
      return;
    }

    // Upload all files
    uploadMutation.mutate(files);
    e.target.value = ''; // Reset input
  };

  const handleDelete = (filename) => {
    if (window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      deleteMutation.mutate(filename);
    }
  };

  const handleBulkDelete = () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedFiles.length} file(s)?`)) {
      bulkDeleteMutation.mutate(selectedFiles);
    }
  };

  const handleSelectFile = (filename) => {
    setSelectedFiles(prev => 
      prev.includes(filename) 
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === clientFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(clientFiles.map(f => f.filename));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="file-stats-wrapper">
      <div className="file-manager-header">
        <h3>Client Files Management</h3>
        <div className="upload-section">
          <label htmlFor="file-upload" className="upload-btn">
            {uploadMutation.isPending ? 'Uploading...' : '📁 Upload Client File(s)'}
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".txt"
            multiple
            onChange={handleFileUpload}
            disabled={uploadMutation.isPending}
            style={{ display: 'none' }}
          />
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            You can select multiple .txt files at once
          </small>
        </div>
      </div>

      {uploadError && <div className="upload-error">{uploadError}</div>}
      {uploadSuccess && <div className="upload-success">{uploadSuccess}</div>}

      {isLoading ? (
        <p className="muted">Loading files...</p>
      ) : clientFiles.length === 0 ? (
        <p className="muted">No client files found. Upload a .txt file to get started.</p>
      ) : (
        <>
          {selectedFiles.length > 0 && (
            <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="bulk-btn"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending || deleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? 'Deleting...' : `🗑️ Delete Selected (${selectedFiles.length})`}
              </button>
              <button
                className="bulk-btn"
                onClick={() => setSelectedFiles([])}
                style={{ background: '#666' }}
              >
                Clear Selection
              </button>
            </div>
          )}
          <div className="scrollable-table-container">
            <table className="file-stats-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedFiles.length === clientFiles.length && clientFiles.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Filename</th>
                  <th>Clients</th>
                  <th>Size</th>
                  <th>Modified</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientFiles.map((file) => (
                  <tr key={file.filename}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.filename)}
                        onChange={() => handleSelectFile(file.filename)}
                      />
                    </td>
                    <td>{file.filename}</td>
                    <td>{file.clientCount}</td>
                    <td>{formatFileSize(file.size)}</td>
                    <td>{formatDate(file.modified)}</td>
                    <td>
                      <button
                        className="delete-file-btn"
                        onClick={() => handleDelete(file.filename)}
                        disabled={deleteMutation.isPending || bulkDeleteMutation.isPending}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function App() {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/verify`);
        if (response.data.valid) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('authToken');
          setIsAuthenticated(false);
        }
      } catch (error) {
        localStorage.removeItem('authToken');
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    queryClient.clear();
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="App">
        <div className="loading">Checking authentication...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Render authenticated dashboard
  return <AuthenticatedDashboard onLogout={handleLogout} />;
}

// Authenticated Dashboard Component
function AuthenticatedDashboard({ onLogout }) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    clientStatus: '',
    location: '',
    receiverEmail: '',
    receiverName: ''
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('no_result');
  const [showSettings, setShowSettings] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    emailIntervalSeconds: 60,
    maxEmailsPerDay: 100
  });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(20);

  // Fetch dashboard overview with React Query
  const {
    data: overview,
    error: overviewError,
    isPending: isOverviewPending,
    isFetching: isOverviewFetching,
  } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/dashboard/overview`);
      return response.data;
    },
    refetchInterval: 30000,
    refetchOnMount: 'always',
  });

  // Fetch email history with React Query
  const {
    data: historyData,
    isPending: isHistoryPending,
    isFetching: isHistoryFetching,
    error: historyError,
  } = useQuery({
    queryKey: ['email', 'history', filters, historyPage, historyPageSize],
    queryFn: async () => {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.clientStatus) params.clientStatus = filters.clientStatus;
      if (filters.location) params.location = filters.location;
      if (filters.receiverEmail) params.receiverEmail = filters.receiverEmail;
      if (filters.receiverName) params.receiverName = filters.receiverName;
      params.page = historyPage;
      params.limit = historyPageSize;
      
      const response = await axios.get(`${API_URL}/dashboard/history`, { params });
      return response.data;
    },
    refetchInterval: 30000,
    keepPreviousData: true,
  });

  // Fetch email settings
  const {
    data: settingsData,
    isPending: isSettingsPending,
    isFetching: isSettingsFetching,
    error: settingsError,
  } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/settings`);
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchInterval: 60000,
    staleTime: 0,
  });

  useEffect(() => {
    if (settingsData) {
      setEmailSettings({
        emailIntervalSeconds: settingsData.emailIntervalSeconds,
        maxEmailsPerDay: settingsData.maxEmailsPerDay || 100,
      });
      if (settingsData.historyPageSize) {
        setHistoryPageSize(settingsData.historyPageSize);
      }
    }
  }, [settingsData]);

  const settings = settingsData || {};
  const systemStats = overview?.systemStats || {};
  const processedFiles = systemStats.filesProcessed || [];
  const currentProcessingFile = systemStats.currentProcessingFile || '';
  const fileStats = systemStats.fileStats || [];
  const templateStats = systemStats.templateStats || [];
  const intervalSeconds = emailSettings.emailIntervalSeconds || settings.emailIntervalSeconds || 60;
  const totalTemplates = systemStats.totalTemplates ?? 0;
  const clientsWithEmails = systemStats.clientsWithEmails ?? 0;
  const currentTemplateFile = systemStats.currentTemplateFile || '';
  const currentTemplateIndex = (systemStats.currentTemplateIndex ?? -1) + 1;

  const formatInterval = (seconds) => {
    const s = Number(seconds);
    if (s < 1) return `${s} seconds`;
    if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
    const minutes = s / 60;
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  };

  const intervalLabel = formatInterval(intervalSeconds);

  const normalizeLink = (value = '') => {
    if (!value) return '';
    const trimmed = value.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const parseSocialLinks = (value = '') => {
    return value
      .split(/[,\s\n]+/)
      .map(link => link.trim())
      .filter(Boolean);
  };


  // Extract email history from the data
  const emailHistory = historyData?.emails || [];
  const totalPages = historyData?.totalPages || 1;
  const totalCount = historyData?.totalCount || 0;
  const currentPage = historyData?.currentPage || historyPage;
  const serverPageSize = historyData?.pageSize || historyPageSize;
  const effectivePageSize = serverPageSize;
  const startEntry = totalCount === 0 ? 0 : (currentPage - 1) * effectivePageSize + 1;
  const endEntry = totalCount === 0 ? 0 : Math.min(startEntry + emailHistory.length - 1, totalCount);

  // Mutation for updating email settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings) => {
      const response = await axios.put(`${API_URL}/settings`, newSettings);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      if (data?.settings) {
        setEmailSettings({
          emailIntervalSeconds: data.settings.emailIntervalSeconds,
          maxEmailsPerDay: data.settings.maxEmailsPerDay,
        });
      }
      alert('Settings updated successfully! Changes are applied immediately.');
    },
  });

  useEffect(() => {
    setSelectedIds([]);
  }, [historyData]);

  useEffect(() => {
    if (historyData?.currentPage && historyData.currentPage !== historyPage) {
      setHistoryPage(historyData.currentPage);
    }
    if (historyData?.pageSize && historyData.pageSize !== historyPageSize) {
      setHistoryPageSize(historyData.pageSize);
    }
  }, [historyData?.currentPage, historyData?.pageSize, historyPage, historyPageSize]);

  // Mutation for updating client status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ emailLogId, clientStatus }) => {
      return await axios.put(`${API_URL}/emails/client-status/${emailLogId}`, { 
        status: clientStatus 
      });
    },
    onSuccess: () => {
      // Invalidate and refetch queries after successful update
      queryClient.invalidateQueries({ queryKey: ['email', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
    },
  });

  // Mutation for bulk update
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ clientIds, status }) => {
      const promises = [
        axios.put(`${API_URL}/dashboard/bulk-update-status`, {
          clientIds,
          status
        })
      ];
      
      // Update email logs
      for (const id of clientIds) {
        promises.push(
          axios.put(`${API_URL}/emails/client-status/${id}`, { status })
        );
      }
      
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      setSelectedIds([]); // Clear selection after success
    },
  });

  // Mutation for pause/resume
  const pauseResumeMutation = useMutation({
    mutationFn: async (action) => {
      const response = await axios.post(`${API_URL}/dashboard/${action}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
    },
  });

  // Update client status
  const updateClientStatus = async (emailLogId, clientStatus) => {
    updateStatusMutation.mutate({ emailLogId, clientStatus });
  };

  // Bulk update status
  const handleBulkUpdate = async () => {
    if (selectedIds.length === 0) return;
    bulkUpdateMutation.mutate({ clientIds: selectedIds, status: bulkStatus });
  };

  // Pause sending
  const handlePause = () => {
    pauseResumeMutation.mutate('pause');
  };

  // Resume sending
  const handleResume = () => {
    pauseResumeMutation.mutate('resume');
  };

  // Handle settings update
  const handleSettingsUpdate = () => {
    updateSettingsMutation.mutate(emailSettings);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPage(1);
  };

  const handleLocationChange = (value) => {
    setFilters(prev => ({ ...prev, location: value }));
    setHistoryPage(1);
  };

  const handlePageChange = (direction) => {
    setHistoryPage(prev => {
      if (direction === 'prev') {
        return Math.max(prev - 1, 1);
      }
      if (direction === 'next') {
        return Math.min(prev + 1, totalPages);
      }
      return prev;
    });
  };

  const handlePageSizeChange = async (value) => {
    const size = parseInt(value, 10) || 20;
    setHistoryPageSize(size);
    setHistoryPage(1);
    
    // Save to database
    try {
      await axios.put(`${API_URL}/settings`, { historyPageSize: size });
    } catch (error) {
      console.error('Failed to save page size:', error);
    }
  };

  // Handle settings change
  const handleSettingsChange = (field, value) => {
    setEmailSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Convert to Japan time
  const toJapanTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', { timeZone: 'Asia/Tokyo' });
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-title">
          <h1>Email Sending Platform Dashboard</h1>
          {overview?.systemStats?.isPaused ? (
            <span className="status-badge status-paused">
              ⏸️ Paused
            </span>
          ) : (
            <span className="status-badge status-active">
              ▶️ Active
            </span>
          )}
        </div>
        <div className="header-buttons">
          <button 
            className="settings-btn"
            onClick={() => setShowSettings(!showSettings)}
          >
            ⚙️ Settings
          </button>
          {overview?.systemStats?.isPaused ? (
            <button 
              className="resume-btn"
              onClick={handleResume}
              disabled={pauseResumeMutation.isPending}
            >
              {pauseResumeMutation.isPending ? '⏳ Resuming...' : '▶️ Resume Sending'}
            </button>
          ) : (
            <button 
              className="pause-btn"
              onClick={handlePause}
              disabled={pauseResumeMutation.isPending}
            >
              {pauseResumeMutation.isPending ? '⏳ Pausing...' : '⏸️ Pause Sending'}
            </button>
          )}
          <button 
            className="logout-btn"
            onClick={onLogout}
          >
            🚪 Logout
          </button>
        </div>
        {updateStatusMutation.isPending && (
          <p className="updating-status">Updating status...</p>
        )}
        {bulkUpdateMutation.isPending && (
          <p className="updating-status">Bulk updating...</p>
        )}
        {isOverviewPending && <p className="info-status">Loading dashboard...</p>}
        {isHistoryPending && <p className="info-status">Loading history...</p>}
        {(overviewError || historyError || settingsError) && (
          <p className="error-status">
            {overviewError && `Error loading dashboard: ${overviewError.message}`}
            {historyError && ` | Error loading history: ${historyError.message}`}
            {settingsError && ` | Error loading settings: ${settingsError.message}`}
          </p>
        )}
        {(isOverviewFetching || isHistoryFetching || isSettingsFetching) && !isOverviewPending && !isHistoryPending && (
          <p className="info-status">Refreshing data...</p>
        )}
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel">
          <h3>Email Sending Settings</h3>
          {isSettingsPending && <p className="info-status">Loading latest settings...</p>}
          <div className="settings-grid">
            <div className="setting-item">
              <label>Send Interval:</label>
              <select 
                value={intervalSeconds}
                onChange={(e) => handleSettingsChange('emailIntervalSeconds', parseFloat(e.target.value))}
              >
                {(settings.availableIntervals || [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 60, 120, 180, 300]).map(option => (
                  <option key={option} value={option}>
                    {formatInterval(option)}
                  </option>
                ))}
              </select>
            </div>
            <div className="setting-item">
              <label>Max Emails Per Day:</label>
              <input
                type="number"
                min="1"
                max="10000"
                value={emailSettings.maxEmailsPerDay || 100}
                onChange={(e) => handleSettingsChange('maxEmailsPerDay', parseInt(e.target.value, 10) || 100)}
              />
            </div>
          </div>
          <div className="settings-info">
            <p><strong>Current:</strong> Scheduling one email every {intervalLabel}</p>
            <p><strong>Daily Limit:</strong> {emailSettings.maxEmailsPerDay || settings.maxEmailsPerDay || 100} emails per day</p>
            <p><strong>Today's Count:</strong> {overview?.systemStats?.dailyEmailsSent || 0} / {emailSettings.maxEmailsPerDay || settings.maxEmailsPerDay || 100}</p>
          </div>
          <button 
            className="update-settings-btn"
            onClick={handleSettingsUpdate}
            disabled={updateSettingsMutation.isPending}
          >
            {updateSettingsMutation.isPending ? 'Updating...' : 'Update Settings'}
          </button>
          {updateSettingsMutation.isError && (
            <p className="error-status">{updateSettingsMutation.error?.response?.data?.error || updateSettingsMutation.error?.message}</p>
          )}
        </div>
      )}

      {overview && !isOverviewPending && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Emails Sent</h3>
              <p className="stat-value">{overview.systemStats?.totalEmailsSent || 0}</p>
              <small>Since start</small>
            </div>
            
            <div className="stat-card">
              <h3>Emails Remaining</h3>
              <p className="stat-value">{overview.systemStats?.remainingEmails ?? 0}</p>
              <small>Left to send</small>
            </div>
            
            <div className="stat-card">
              <h3>Total Clients</h3>
              <p className="stat-value">{overview.totalClients || 0}</p>
              <small>In database</small>
            </div>
            
            <div className="stat-card">
              <h3>Email Templates</h3>
              <p className="stat-value">{totalTemplates}</p>
              <small>Available templates</small>
            </div>
            
            <div className="stat-card">
              <h3>Clients with Emails</h3>
              <p className="stat-value">{clientsWithEmails}</p>
              <small>Valid email addresses</small>
            </div>
          </div>

          <div className="files-summary">
            <div className="files-summary-grid">
              <div className="files-summary-card">
                <h3>Current Processing File</h3>
                <p className="summary-value">{currentProcessingFile || '—'}</p>
              </div>
              <div className="files-summary-card">
                <h3>Current Template</h3>
                <p className="summary-value">{currentTemplateFile || '—'}</p>
                <p className="muted">
                  {currentTemplateFile
                    ? `Template #${Math.max(currentTemplateIndex, 1)} of ${totalTemplates}`
                    : 'No template selected'}
                </p>
              </div>
              <div className="files-summary-card">
                <h3>Processed Files</h3>
                {processedFiles.length > 0 ? (
                  <ul>
                    {processedFiles.map((file) => (
                      <li key={file}>{file}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">No files processed yet.</p>
                )}
              </div>
            </div>

            {fileStats.length > 0 && (
              <div className="file-stats-wrapper">
                <h3>Per-File Delivery Status</h3>
                <div className="scrollable-donut-container">
                  <div className="donut-grid">
                  {fileStats.map((stat) => {
                    // Use clientsWithEmails as the base for percentage calculations
                    const total = stat.clientsWithEmails || 1;
                    const sent = stat.sent || 0;
                    const failed = stat.failed || 0;
                    const pending = stat.pending || 0;
                    const remaining = stat.remainingClients || 0;
                    const skipped = stat.skipped || 0; // Get skipped from backend
                    
                    const sentPercent = (sent / total) * 100;
                    const failedPercent = (failed / total) * 100;
                    const pendingPercent = (pending / total) * 100;
                    const skippedPercent = (skipped / total) * 100;
                    
                    const isCurrentFile = stat.file === currentProcessingFile;
                    
                    return (
                      <div key={stat.file} className={`donut-card ${isCurrentFile ? 'current-processing' : ''}`}>
                        <h4>{stat.file}</h4>
                        <svg viewBox="0 0 36 36" className="donut-chart">
                          <circle
                            className="donut-ring"
                            cx="18"
                            cy="18"
                            r="15.915"
                            fill="transparent"
                            stroke="#2a2a3e"
                            strokeWidth="3"
                          />
                          <circle
                            className="donut-segment donut-sent"
                            cx="18"
                            cy="18"
                            r="15.915"
                            fill="transparent"
                            stroke="#3dd68c"
                            strokeWidth="3"
                            strokeDasharray={`${sentPercent} ${100 - sentPercent}`}
                            strokeDashoffset="25"
                          />
                          <circle
                            className="donut-segment donut-failed"
                            cx="18"
                            cy="18"
                            r="15.915"
                            fill="transparent"
                            stroke="#ff6b6b"
                            strokeWidth="3"
                            strokeDasharray={`${failedPercent} ${100 - failedPercent}`}
                            strokeDashoffset={25 - sentPercent}
                          />
                          <circle
                            className="donut-segment donut-pending"
                            cx="18"
                            cy="18"
                            r="15.915"
                            fill="transparent"
                            stroke="#ffd93d"
                            strokeWidth="3"
                            strokeDasharray={`${pendingPercent} ${100 - pendingPercent}`}
                            strokeDashoffset={25 - sentPercent - failedPercent}
                          />
                          <circle
                            className="donut-segment donut-skipped"
                            cx="18"
                            cy="18"
                            r="15.915"
                            fill="transparent"
                            stroke="#9b59b6"
                            strokeWidth="3"
                            strokeDasharray={`${skippedPercent} ${100 - skippedPercent}`}
                            strokeDashoffset={25 - sentPercent - failedPercent - pendingPercent}
                          />
                          <text x="18" y="20.5" className="donut-percent">
                            {Math.round((sent / total) * 100)}%
                          </text>
                        </svg>
                        <div className="donut-legend">
                          <div className="legend-item">
                            <span className="legend-dot sent"></span>
                            <span>Sent: {sent}</span>
                          </div>
                          <div className="legend-item">
                            <span className="legend-dot failed"></span>
                            <span>Failed: {failed}</span>
                          </div>
                          <div className="legend-item">
                            <span className="legend-dot pending"></span>
                            <span>Pending: {pending}</span>
                          </div>
                          {skipped > 0 && (
                            <div className="legend-item">
                              <span className="legend-dot skipped"></span>
                              <span>Skipped: {skipped}</span>
                            </div>
                          )}
                          <div className="legend-item">
                            <span className="legend-dot remaining"></span>
                            <span>Remaining: {remaining}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
            )}

            {templateStats.length > 0 && (
              <div className="file-stats-wrapper">
                <h3>Templates by File</h3>
                <table className="file-stats-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Template Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateStats.map((stat) => (
                      <tr key={stat.file}>
                        <td>{stat.file}</td>
                        <td>{stat.templateCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Client Files Management */}
            <ClientFilesManager API_URL={API_URL} />

            {/* Email Template Editor */}
            <EmailTemplateEditor API_URL={API_URL} />

            {/* SendGrid & From Email (overrides config.env) */}
            <SendGridAndFromEmail API_URL={API_URL} />

          </div>
        </>
      )}

      <div className="filters-section">
        <h2>Email History</h2>
        
        <div className="filters">
          <select 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>

          <select 
            value={filters.clientStatus} 
            onChange={(e) => handleFilterChange('clientStatus', e.target.value)}
          >
            <option value="">All Results</option>
            <option value="no_result">No Result</option>
            <option value="received">Received</option>
            <option value="rejected">Rejected</option>
            <option value="website">Website</option>
          </select>

          <input 
            type="text" 
            placeholder="Filter by Location..." 
            value={filters.location}
            onChange={(e) => handleLocationChange(e.target.value)}
          />

          <input 
            type="text" 
            placeholder="Filter by Email..." 
            value={filters.receiverEmail}
            onChange={(e) => handleFilterChange('receiverEmail', e.target.value)}
          />

          <input 
            type="text" 
            placeholder="Filter by Name..." 
            value={filters.receiverName}
            onChange={(e) => handleFilterChange('receiverName', e.target.value)}
          />
        </div>

        <div className="bulk-actions">
          <div className="status-radios">
            <label>
              <input 
                type="radio" 
                value="no_result" 
                checked={bulkStatus === 'no_result'}
                onChange={(e) => setBulkStatus(e.target.value)}
              />
              No Result
            </label>
            <label>
              <input 
                type="radio" 
                value="received" 
                checked={bulkStatus === 'received'}
                onChange={(e) => setBulkStatus(e.target.value)}
              />
              Received
            </label>
            <label>
              <input 
                type="radio" 
                value="rejected" 
                checked={bulkStatus === 'rejected'}
                onChange={(e) => setBulkStatus(e.target.value)}
              />
              Rejected
            </label>
          </div>
          
          <button 
            onClick={handleBulkUpdate}
            disabled={selectedIds.length === 0 || bulkUpdateMutation.isPending}
            className="bulk-btn"
          >
            {bulkUpdateMutation.isPending ? 'Updating...' : `Update Selected (${selectedIds.length})`}
          </button>
        </div>
      </div>

      <div className="history-table">
        {isHistoryPending ? (
          <div className="loading">Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox"
                    checked={selectedIds.length === emailHistory.length && emailHistory.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(emailHistory.map(h => h._id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>
                <th>Receiver</th>
                <th>Name</th>
                <th>Location</th>
                <th>Website</th>
                <th>Profile URL</th>
                <th>Social</th>
                <th>Subject</th>
                <th>Time (Japan)</th>
                <th>Status</th>
                <th>Sender Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {emailHistory.map((email) => (
                <tr key={email._id} className={email.status}>
                  <td>
                    <input 
                      type="checkbox"
                      checked={selectedIds.includes(email._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds([...selectedIds, email._id]);
                        } else {
                          setSelectedIds(selectedIds.filter(id => id !== email._id));
                        }
                      }}
                    />
                  </td>
                  <td>{email.receiverEmail}</td>
                  <td>{email.receiverName || '-'}</td>
                  <td>{email.receiverLocation || '-'}</td>
                  <td>
                    {email.receiverWebsite ? (
                      <a
                        href={normalizeLink(email.receiverWebsite)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-pill"
                      >
                        Website
                      </a>
                    ) : '—'}
                  </td>
                  <td>
                    {email.receiverProfileUrl ? (
                      <a
                        href={normalizeLink(email.receiverProfileUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-pill"
                      >
                        Profile
                      </a>
                    ) : '—'}
                  </td>
                  <td>
                    {email.receiverSocial ? (
                      <div className="link-list">
                        {parseSocialLinks(email.receiverSocial).map((link, idx) => (
                          <a
                            key={`${email._id}-social-${idx}`}
                            href={normalizeLink(link)}
          target="_blank"
          rel="noopener noreferrer"
                            className="link-pill"
                          >
                            Social {idx + 1}
                          </a>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="subject-cell">{email.subject}</td>
                  <td>{toJapanTime(email.sentAt)}</td>
                  <td>
                    <span className={`status-badge ${email.status}`}>
                      {email.status}
                      {email.isRetry && (
                        <span className="retry-indicator" title={`Retry attempt ${email.retryAttempt}`}>
                          🔄
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{email.senderEmail || '-'}</td>
                  <td>
                    <button 
                      className="view-btn"
                      onClick={() => {
                      const reason = email.error && email.error.trim().length > 0 ? `\n\nReason: ${email.error}` : '';
                      alert(`Message:\n\n${email.message}${reason}`);
                      }}
                    >
                      View Message
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="pagination-controls">
          <div className="pagination-info">
            {totalCount > 0
              ? `Showing ${startEntry}-${endEntry} of ${totalCount}`
              : 'No records'}
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => handlePageChange('prev')}
              disabled={currentPage <= 1 || isHistoryFetching}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => handlePageChange('next')}
              disabled={currentPage >= totalPages || isHistoryFetching}
            >
              Next
            </button>
          </div>
          <div className="pagination-size">
            <label>Rows per page:</label>
            <select
              value={historyPageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              disabled={isHistoryFetching}
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
